import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { getConfigAiTutor, postAiTutorEvent } from './data/api';

function useDelayUnmount(isMounted, delayTime) {
  const [showDiv, setShowDiv] = useState(false);

  useEffect(() => {
    let timeoutId;

    if (isMounted && !showDiv) {
      setShowDiv(true);
    } else if (!isMounted && showDiv) {
      timeoutId = setTimeout(() => setShowDiv(false), delayTime); // delay our unmount
    }

    return () => clearTimeout(timeoutId); // cleanup mechanism for effects , the use of setTimeout generate a sideEffect
  }, [isMounted, delayTime, showDiv]);

  return showDiv;
}

const useAiTutor = courseId => {
  const [isOpen, setIsOpen] = useState(false);
  const showDiv = useDelayUnmount(isOpen, 250);
  const chatEndRef = useRef(null);
  const queryClient = useQueryClient();

  // Завантаження історії повідомлень
  const { data, isLoading } = useQuery(['aiConfig', courseId], () => getConfigAiTutor(courseId), {
    select: d => ({
      isEnabled: d?.is_enabled,
      history: d?.history?.sort((a, b) => new Date(a.created) - new Date(b.created)) || [],
    }),
  });

  // Збереження історії в кеш
  useEffect(() => {
    if (data?.history) {
      queryClient.setQueryData(
        'aiMessages',
        data.history.map(msg => ({
          message: msg.content,
          isReceived: msg.type === 'ai',
        })),
      );
    }
  }, [data, queryClient]);

  // Мутація для надсилання повідомлення
  const mutation = useMutation(postAiTutorEvent, {
    onMutate: async ({ formData }) => {
      await queryClient.cancelQueries('aiMessages');
      const previousMessages = queryClient.getQueryData('aiMessages') || [];

      const userMessage = formData.get('message');
      queryClient.setQueryData('aiMessages', [
        ...previousMessages,
        { message: userMessage, isReceived: false },
      ]);

      return { previousMessages };
    },
    onSuccess: responseData => {
      const replyMessage = responseData.response || 'Немає відповіді';

      queryClient.setQueryData('aiMessages', old => [
        ...(old || []),
        { message: replyMessage, isReceived: true },
      ]);
    },
    onError: (err, newMessage, context) => {
      queryClient.setQueryData('aiMessages', context.previousMessages);
    },
  });

  const toggleOpen = () => {
    setIsOpen(!isOpen);
  };

  return {
    isOpen,
    showDiv,
    isLoading,
    isEnabled: data?.isEnabled,
    messages: queryClient.getQueryData('aiMessages') || [],
    isSending: mutation.isLoading,
    sendMessage: formData => mutation.mutate({ formData, courseId }),
    toggleOpen,
    chatEndRef,
  };
};

export default useAiTutor;
